import axios from "axios"
import qs from "qs"
import { Role } from "src/UserService"

export interface GatedCommunityCreationDto {
  name: string
  memberIds?: string[]
}

export interface GatedCommunityRecord {
  id: string
  name?: string
}

/** Profile status for a member in a gated community (matches backend ProfileStatus). */
export type GatedCommunityProfileStatus = "GATED_COMMUNITY_MEMBER" | "GATED_COMMUNITY_ADMIN"

export interface GatedCommunityMembership {
  communityId: string
  communityName: string
  profile: GatedCommunityProfileStatus
}

export interface GatedCommunityMemberRecord {
  id: string
  firstName: string | null
  lastName: string | null
  email: string
  profile: GatedCommunityProfileStatus | null
  roles: Role[]
  communities: GatedCommunityMembership[]
  isPending?: boolean
  phoneNumber?: string
}

export type GatedCommunityMembersSortBy = "USER_NAME" | "FIRST_NAME" | "LAST_NAME" | "EMAIL" | "PROFILE"
export type GatedCommunitySortOrder = "ASC" | "DESC"

export interface GatedCommunityMembersParams {
  offset?: number
  limit?: number
  sort?: GatedCommunityMembersSortBy
  sortOrder?: GatedCommunitySortOrder
  search?: string
  roles?: Role[]
}

export interface GatedCommunityMembersResponse {
  results: GatedCommunityMemberRecord[]
  totalCount: number
}

export function getGatedCommunityMembers(gatedCommunityId: string, params: GatedCommunityMembersParams = {}) {
  const { offset = 0, limit = 25, sort = "LAST_NAME", sortOrder = "ASC", search, roles } = params
  return axios
    .get<GatedCommunityMembersResponse>(`/api/wfl/groups_management/v2/communities/${gatedCommunityId}/members`, {
      params: { offset, limit, sort, sortOrder, search, roles },
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "repeat" })
    })
    .then((r) => ({ data: r.data.results, totalCount: r.data.totalCount }))
}

export function getGatedCommunityMember(
  gatedCommunityId: string,
  memberId: string
): Promise<GatedCommunityMemberRecord> {
  return axios
    .get<GatedCommunityMemberRecord>(
      `/api/wfl/groups_management/v2/communities/${gatedCommunityId}/members/${memberId}`
    )
    .then((r) => r.data)
}

export function createGatedCommunity(dto: GatedCommunityCreationDto) {
  return axios
    .post<void>("/api/wfl/groups_management/v2/communities", dto)
    .then((r) => r.data)
}

export function addUserToGatedCommunity(
  gatedCommunityId: string,
  userId: string,
  profile: GatedCommunityProfileStatus = "GATED_COMMUNITY_MEMBER"
) {
  return axios
    .put<void>(`/api/wfl/groups_management/v2/communities/${gatedCommunityId}/members/${userId}`, null, {
      params: { profile }
    })
    .then((r) => r.data)
}

export function setUserProfileInGatedCommunity(
  gatedCommunityId: string,
  userId: string,
  profile: GatedCommunityProfileStatus
) {
  return axios
    .post<void>(`/api/wfl/groups_management/v2/communities/${gatedCommunityId}/members/${userId}/profile`, { profile })
    .then((r) => r.data)
}

export function deleteUserFromGatedCommunity(gatedCommunityId: string, userId: string) {
  return axios
    .delete<void>(`/api/wfl/groups_management/v2/communities/${gatedCommunityId}/members/${userId}`)
    .then((r) => r.data)
}
